<template>
  <div class="customers-page">
    <div :class="{ 'has-pagination': hasPagination }" class="main-content">
      <div class="customers-page__header d-flex flex-column i-py-8 i-px-12">
        <div class="d-flex justify-content-between align-items-center">
          <div class="d-flex flex-row align-items-center i-gap-6">
            <router-link v-if="isFromAnalytics" to="/analytics">
              <i-button
                data-testid="customers-page-back-button"
                left-icon="medium-arrows-left"
                :type="iButtonTypes.NEUTRAL"
                :button-style="iButtonStyles.GHOST"
              />
            </router-link>
            <div
              data-testid="customers-page-header-title"
              class="title i-text-heading-5 i-text-neutral-120"
            >
              Customers
            </div>
          </div>
          <div
            class="customers-page__header-buttons d-flex justify-space-between align-items-center i-gap-4 i-ml-6"
          >
            <i-menu
              v-if="canDownloadBusinessComplianceCSV"
              :close-on-item-click="true"
              :width="200"
              @i-menu-open-changed="(value) => (isDownloadMenuOpen = value)"
            >
              <template #trigger>
                <i-button
                  label="Download"
                  v-read-only-check
                  type="neutral"
                  subtype="outline"
                  :right-icon="
                    isDownloadMenuOpen
                      ? 'small_chevron_up'
                      : 'small_chevron_down'
                  "
                />
              </template>
              <template #items>
                <i-menu-item
                  data-testid="csv-export-button"
                  label="CSV of Customers"
                  :loading="exportLoading"
                  @item-click="$emit('export-customers')"
                />
                <i-menu-item
                  data-testid="compliance-results-download-button"
                  label="CSV of Compliance Results"
                  :loading="downloadLoading"
                  @item-click="downloadComplianceResults"
                />
              </template>
            </i-menu>
            <i-button
              v-if="!canDownloadBusinessComplianceCSV"
              id="csvExport"
              data-testid="csv-export-button"
              label="Export"
              v-read-only-check
              type="neutral"
              subtype="outline"
              :loading="exportLoading"
              @button-click="$emit('export-customers')"
            ></i-button>
            <i-tooltip
              :target="`#csvExport`"
              title="Export CSV of customers matching current query"
              :alignment="iTooltipAlignmentMode.BOTTOM_MIDDLE"
            />
            <i-menu
              v-if="canViewBusinessComplianceAgent"
              :close-on-item-click="true"
              :width="200"
              @i-menu-open-changed="(value) => (isBulkUploadMenuOpen = value)"
            >
              <template #trigger>
                <i-button
                  label="Bulk Upload"
                  v-read-only-check
                  type="neutral"
                  subtype="outline"
                  :disabled="isUploadAccessBlocked"
                  :right-icon="
                    isBulkUploadMenuOpen
                      ? 'small_chevron_up'
                      : 'small_chevron_down'
                  "
                />
              </template>
              <template #items>
                <i-menu-item
                  data-testid="bulk-upload-button"
                  label="Documents"
                  :loading="exportLoading"
                  @item-click="toggleBulkUpload('document')"
                />
                <i-menu-item
                  data-testid="bulk-upload-customers-button"
                  label="Customers"
                  :loading="downloadLoading"
                  @item-click="toggleBulkUpload('customer')"
                />
              </template>
            </i-menu>

            <i-button
              v-if="!canViewBusinessComplianceAgent"
              label="Bulk Upload"
              v-read-only-check
              data-testid="bulk-upload-button"
              type="neutral"
              subtype="outline"
              :disabled="isUploadAccessBlocked"
              @button-click="toggleBulkUpload('document')"
            ></i-button>

            <i-button
              label="New Customer"
              v-read-only-check
              data-testid="new-customer-button"
              :disabled="isUploadAccessBlocked"
              @button-click="createNewCustomer()"
            ></i-button>

            <chat-toggle-button v-if="isChatAgentEnabled" />
          </div>
        </div>

        <BulkUploadCard
          :is-visible="showBulkDocumentUpload"
          title="Bulk Upload Documents"
          subtitle="Upload a ZIP file containing all the files you want to analyze"
          :accepted-extensions="['zip']"
          :is-hidden="hideDocumentUploadContainer"
          :show-success-message="showDocumentUploadCompleteMessage"
          success-message="Your documents are currently being organized by customer. Once processing is complete, we'll notify you via email."
          dropzone-ref="dropzone"
          file-icon="medium-file-zip"
          @close="hideBulkDocumentUploadContainer"
          @files-selected="onDocumentFilesSelected"
        />

        <BulkUploadCard
          :is-visible="showBulkCustomerUpload"
          title="Bulk Upload Customers"
          subtitle="Upload a CSV file containing customer information"
          :accepted-extensions="['csv']"
          :is-hidden="hideCustomerUploadContainer"
          :show-success-message="showCustomerUploadCompleteMessage"
          success-message="Your customers are currently being processed. Once complete, they will appear in your customer list."
          :error-message="customerUploadError"
          dropzone-ref="customerDropzone"
          file-icon="medium-file-text-2"
          documentation-link="https://docs.inscribe.ai/docs/bulk-upload-customers-via-csv"
          @close="hideBulkCustomerUploadContainer"
          @files-selected="onCustomerFilesSelected"
        />
      </div>

      <div class="customers-page__search-filters-container i-px-12">
        <div class="customers-page__search-filters i-py-11">
          <CustomerSearchFilter />

          <CustomerRiskTabFilters class="i-mt-11 i-mb-6" />

          <customer-filters
            ref="customerFilters"
            :has-selected-filters="hasSelectedFilters"
            @reset-filters="resetFilters"
          />
        </div>
      </div>

      <div
        class="customers-table-container i-mt-10 i-px-12"
        :class="{ 'extra-padding': hasFooter && !hasPagination }"
      >
        <customers-table />
      </div>
    </div>

    <div
      v-if="hasFooter"
      class="footer d-flex flex-row justify-content-between i-py-10"
    >
      <div class="i-pagination-wrapper d-flex flex-row i-gap-6 i-px-12">
        <i-pagination
          v-if="hasPagination"
          size="md"
          :total-items="customersMetadata?.pagination.total"
          :items-per-page="10"
          :selected-page="pageNumber"
          @page-click="onPageChange"
        ></i-pagination>
        <i-table-columns-filter
          table-id="customers-table"
          @columns-selection-changed="onColumnsUpdated"
        ></i-table-columns-filter>
      </div>
    </div>
    <router-view />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';
import {
  iAlertIcon,
  iAlertMessage,
  iIcon,
  iButton,
  iButtonStyles,
  iButtonTypes,
  iDropzone,
  iPagination,
  iSpinner,
  iTableColumnsFilter,
  iTooltip,
  iTooltipAlignmentMode,
  showToast,
  iMenu,
  iMenuItem,
} from '@inscribe/signal';
import * as documentStates from '@/constants/documentStates';
import CustomersTable from './CustomersTable';
import CustomerFilters from './CustomerFilters';
import CustomerRiskTabFilters from '@/V2/customers/CustomerRiskTabFilters';
import { BULK_UPLOAD } from '../../constants/onboardingChecklist';
import CustomerSearchFilter from '@/V2/components/filters/CustomerSearchFilter';
import {
  deleteAllQueryParams,
  updateQueryParams,
  getAndSyncQueryParams,
} from '@/utils/queryParams';
import { formatDateTime } from '@/utils/date';
import axios from 'axios';
import BulkUploadCard from '@/V2/components/BulkUploadCard.vue';
import { downloadBlob } from '@/utils/download';
import { writeStorage } from '@/utils/storage';
import ChatToggleButton from '@/V2/chat/ChatToggleButton.vue';

const SELECTED_COLUMNS_STORAGE_KEY = 'customersTableColumns';

export default {
  components: {
    BulkUploadCard,
    CustomerFilters,
    CustomerSearchFilter,
    CustomerRiskTabFilters,
    CustomersTable,
    iAlertIcon,
    iAlertMessage,
    iIcon,
    iButton,
    iDropzone,
    iPagination,
    iSpinner,
    iTableColumnsFilter,
    iTooltip,
    iMenu,
    iMenuItem,
    ChatToggleButton,
  },
  props: {
    exportLoading: {
      type: Boolean,
      default: false,
    },
    downloadLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      pageNumber: 1,
      acceptedFileTypes: [
        'application/zip',
        'application/octet-stream',
        'application/x-zip-compressed',
        'multipart/x-zip',
      ],
      showBulkDocumentUpload: false,
      showBulkCustomerUpload: false,
      showDocumentUploadCompleteMessage: false,
      showCustomerUploadCompleteMessage: false,
      documentStates,
      hideDocumentUploadContainer: false,
      hideCustomerUploadContainer: false,
      iButtonStyles,
      iButtonTypes,
      iTooltipAlignmentMode,
      params: getAndSyncQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
      }),
      customerUploadError: null,
      isDownloadMenuOpen: false,
      isBulkUploadMenuOpen: false,
    };
  },
  computed: {
    ...mapGetters([
      'getCachedCustomers',
      'canUseCredit',
      'isUploadAccessBlocked',
      'canViewBusinessComplianceAgent',
      'canDownloadBusinessComplianceCSV',
      'isChatAgentEnabled',
    ]),
    ...mapState(['bulkUploads']),
    hasFooter() {
      return this.customersMetadata?.pagination.total > 0;
    },
    customers() {
      return this.getCachedCustomers(this.params)?.data;
    },
    customersMetadata() {
      return this.getCachedCustomers(this.params)?.metadata;
    },
    hasPagination() {
      return (
        this.customersMetadata &&
        this.customersMetadata.pagination.number_of_pages > 1
      );
    },
    isFromAnalytics() {
      return this.$route.query.return_to === 'analytics';
    },
    hasSelectedFilters() {
      return this.params && Object.keys(this.params).length > 0;
    },
  },
  watch: {
    '$route.fullPath': {
      handler: function (newFullPath) {
        this.params = getAndSyncQueryParams({
          fullPath: newFullPath,
          router: this.$router,
          store: this.$store,
        });
      },
    },
    '$route.query.page': {
      immediate: true,
      handler(newPage) {
        this.pageNumber = parseInt(newPage) || 1;
      },
    },
  },
  mounted() {
    this.getPendingBulkUploads().then((response) => {
      if (response.data.uploads) {
        const pendingUploads = response.data.uploads.map((upload) => ({
          ...upload,
          progress: 100,
          success: upload.state === documentStates.PROCESSING,
        }));
        this.setPendingBulkUploads(pendingUploads);
      }
    });
  },
  methods: {
    ...mapActions(['getPendingBulkUploads']),
    ...mapMutations([
      'setPendingBulkUploads',
      'addPendingBulkUpload',
      'updatePendingBulkUpload',
    ]),
    formatDateTime,
    resetFilters() {
      deleteAllQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
      });
    },

    createNewCustomer() {
      deleteAllQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
      });

      this.$router.push({
        name: 'new-customer',
      });
    },

    onPageChange(page) {
      this.pageNumber = page;
      this.$router
        .push({
          path: this.$route.path,
          query: { ...this.$route.query, page: this.pageNumber },
        })
        .catch(() => {});
    },

    onDocumentFilesSelected(files) {
      this.hideDocumentUploadContainer = true;
      const file = files[0];
      const fileUpload = {
        name: file.name,
        size: file.size / 1000000,
        uploadId: crypto.randomUUID(),
        uploadProgress: 0,
        created_at: Date.now(),
      };
      this.addPendingBulkUpload(fileUpload);

      const formData = new FormData();
      formData.append('file', file);
      return axios.post(`api/v2/documents/bulk`, formData).then(
        () => {
          this.updatePendingBulkUpload({
            uploadId: fileUpload.uploadId,
            uploadProgress: 100,
            success: true,
          });

          updateQueryParams({
            fullPath: this.$route.fullPath,
            router: this.$router,
            store: this.$store,
            updates: {
              page: 1,
            },
          });

          this.$store.commit('addCompletedAction', BULK_UPLOAD);
          this.hideDocumentUploadContainer = true;
          this.showDocumentUploadCompleteMessage = true;
        },
        (error) => {
          showToast({
            text: error,
            isError: true,
          });
          this.hideDocumentUploadContainer = true;

          files.forEach((file) => {
            this.updatePendingBulkUpload({
              uploadId: fileUpload.uploadId,
              error,
            });
          });
        },
      );
    },

    onCustomerFilesSelected(files) {
      this.hideCustomerUploadContainer = true;
      this.customerUploadError = null;
      const file = files[0];
      const fileUpload = {
        name: file.name,
        size: file.size / 1000000,
        uploadId: crypto.randomUUID(),
        uploadProgress: 0,
        created_at: Date.now(),
      };
      this.addPendingBulkUpload(fileUpload);

      const formData = new FormData();
      formData.append('file', file);

      return axios.post(`api/v2/customers/bulk`, formData).then(
        () => {
          this.updatePendingBulkUpload({
            uploadId: fileUpload.uploadId,
            uploadProgress: 100,
            success: true,
          });

          updateQueryParams({
            fullPath: this.$route.fullPath,
            router: this.$router,
            store: this.$store,
            updates: {
              page: 1,
            },
          });

          this.$store.commit('addCompletedAction', BULK_UPLOAD);
          this.hideCustomerUploadContainer = true;
          this.showCustomerUploadCompleteMessage = true;
        },
        (error) => {
          this.customerUploadError =
            error?.response?.data?.message || 'Error uploading customers';
          this.hideCustomerUploadContainer = true;

          this.updatePendingBulkUpload({
            uploadId: fileUpload.uploadId,
            error: this.customerUploadError,
          });
        },
      );
    },

    hideBulkDocumentUploadContainer() {
      this.showBulkDocumentUpload = false;
      if (this.showDocumentUploadCompleteMessage) {
        this.hideDocumentUploadContainer = false;
        this.showDocumentUploadCompleteMessage = false;
      }
    },

    hideBulkCustomerUploadContainer() {
      this.showBulkCustomerUpload = false;
      if (this.showCustomerUploadCompleteMessage) {
        this.hideCustomerUploadContainer = false;
        this.showCustomerUploadCompleteMessage = false;
      }
      this.customerUploadError = null;
    },

    toggleBulkUpload(type) {
      if (type === 'document') {
        this.showBulkCustomerUpload = false;
        this.showBulkDocumentUpload = !this.showBulkDocumentUpload;
      } else {
        this.showBulkDocumentUpload = false;
        this.showBulkCustomerUpload = !this.showBulkCustomerUpload;
      }
    },

    async downloadComplianceResults() {
      try {
        const response = await axios({
          url: 'api/v2/internal/agents/business-compliance/report',
          method: 'GET',
          responseType: 'blob',
        });

        downloadBlob(
          response.data,
          `Inscribe Compliance Report ${formatDateTime(new Date(), 'YYYY-MM-DD')}.csv`,
          'text/csv',
        );
      } catch (error) {
        showToast({
          text: 'Error downloading compliance report',
          isError: true,
        });
      }
    },
    onColumnsUpdated(columns) {
      writeStorage(SELECTED_COLUMNS_STORAGE_KEY, columns);
    },
  },
};
</script>

<style lang="scss" scoped>
.customers-page {
  .main-content {
    &.has-pagination {
      padding-bottom: 88px;
    }
  }

  &__header {
    position: sticky;
    top: 0px;
    z-index: 100;
    background: $color-white;
    border-bottom: 1px solid $light-neutral-60;
  }

  &__header-buttons {
    @include respond-to('large') {
      align-items: stretch !important;
    }
  }

  &__search-filters {
    background-color: $light-neutral-default;
  }

  &__bulk-upload {
    border: 1px solid $light-neutral-60 !important;
    background: $light-neutral-default;
    border-radius: map.get($increments, 10) !important;
    padding: map.get($increments, 10) !important;

    .empty-state {
      border: 1px dashed $light-neutral-60;
      border-radius: map.get($increments, 4);

      .icon-wrapper {
        background: $light-neutral-30;
        width: 88px;
        height: 88px;
        border-radius: 50%;
        .i-icon {
          color: $light-neutral-120 !important;
        }
      }
    }
    .upload {
      border: 1px solid $light-neutral-60;
      border-radius: map.get($increments, 8);

      &:not(:last-child) {
        margin-bottom: map.get($increments, 8);
      }

      .icon-wrapper {
        background: $light-primary-30;
        width: 64px;
        height: 64px;
        border-radius: 50%;
        .i-icon {
          color: $light-primary-110 !important;
        }
      }
    }
  }

  .main-content {
    margin: auto;
  }

  .customers-table-container {
    &.extra-padding {
      padding-bottom: 100px;
    }
  }

  .footer {
    position: fixed;
    bottom: 0;
    left: 240px;
    right: 0;
    box-shadow: 0 0 1px rgba(0, 0, 0, 0.75);
    z-index: 100;
    margin: auto;
    background: $color-white;

    @media only screen and (max-width: 900px) {
      left: 64px;
    }

    @media only screen and (max-width: 769px) {
      left: 0;
    }
  }
}
</style>
